<template>
  <div class="payment-method-wrapper">
    <div class="row">
      <h3 v-if="showSelectPayment" class="col xs-12 !pl-0" id="payment-method-title">
        {{ checkoutSharedContent.selectPaymentMethod }}
      </h3>
    </div>
    <div
      v-if="showSelectPayment"
      class="col !pl-0 flex flex-wrap justify-start payment-select-wrapper"
    >
      <vSelect
        class="col xs-12 md-4 lg-5 xl-5 payment-method-input"
        id="payment-method"
        :placeholder="checkoutSharedContent.selectPaymentMethod"
        :options="filteredAvailablePaymentMethods"
        :reduce="(displayName) => displayName.code"
        label="displayName"
        :value="paymentMethod ?? availablePaymentMethods?.[0]"
        @input="setPaymentMethod"
      >
      </vSelect>
      <div
        v-if="isENT"
        class="po-wrapper md:flex mt-1 md:mt-0 md:ml-5 w-full items-center"
      >
        <label for="PONumber">{{
          checkoutSharedContent.paymentMethodNames.PONumber
        }}</label>
        <input
          name="PONumber"
          type="text"
          @input="handlePOChange"
          :value="PONumber"
        />
      </div>
    </div>
    <div v-if="paymentMethod === 'Invoice'" class="po-notice">
      <div class="flex mb-1/2">
        <input
          class="mt-0"
          id="order-form"
          name="sign-order-form"
          type="radio"
          checked
          @change="handlePDFChange"
        />
        <label
          class="mb-0 ml-1/2 font-normal !pb-0 dark-blue-text leading-tight"
          for="order-form"
          >{{ checkoutSharedContent.notices.order_form }}</label
        >
      </div>
      <div class="flex">
        <input
          class="mt-0"
          id="po-document"
          name="sign-order-form"
          type="radio"
          @change="handlePDFChange"
        />
        <label
          class="mb-0 ml-1/2 font-normal !pb-0 dark-blue-text leading-tight"
          for="po-document"
          >{{ checkoutSharedContent.notices.po_document }}</label
        >
      </div>
      <div v-if="sendPDF">
        <p class="!pb-0 dark-blue-text leading-tight !mt-1/2">
          {{
            checkoutSharedContent.notices.owner_email.replace(
              "__owner_email__",
              ownerEmail
            )
          }}
        </p>
        <a :href="`mailto:${ownerEmail}?subject=WEBSTORE ORDER PO`"
          >Click here to send your PDF</a
        >
      </div>
    </div>
    <div v-if="showSelectPayment" class="row !pl-0">
      <p
        v-if="!isPmUpdate"
        id="disclaimer"
        class="col leading-tight xs-12 md-10 !pl-0"
      >
        {{ disclaimer }}
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import vSelect from "vue-select";
import "vue-select/src/scss/vue-select.scss";

import {
  USD_CREDIT_CARD_LIMIT,
  EUR_CREDIT_CARD_LIMIT,
  GBP_CREDIT_CARD_LIMIT,
} from "../../../settings";
import { snakeCase } from "../../../utils";
import { MidmarketCheckout } from "../../../settings";

export default {
  props: {
    ownerEmail: {
      type: String,
    },
    availablePaymentMethods: {
      type: Array,
      default: () => [],
      required: false,
    },
  },

  components: { vSelect },

  data() {
    return {
      creditCardLimit: {
        USD: USD_CREDIT_CARD_LIMIT,
        EUR: EUR_CREDIT_CARD_LIMIT,
        GBP: GBP_CREDIT_CARD_LIMIT,
      },
      SFDCPaymentTypeToVariableName: {
        "Direct Monthly": "monthly",
        "Direct Annual": "annual",
        "Upfront Payments": "upfront",
      },
      sendPDF: false,
    };
  },
  computed: {
    ...mapGetters("content", ["checkoutSharedContent", "checkoutContent"]),

    ...mapGetters("checkout", [
      "selectedPaymentType",
      "checkoutType",
      "currencyIsoCode",
      "paymentMethod",
      "isPmUpdate",
      "isSelfServiced",
      "isENT",
    ]),

    ...mapGetters("shoppingCart", ["productCostsInfo", "softwareCart"]),

    ...mapGetters("billing", ["PONumber", "country"]),

    paymentType() {
      if (this.isPmUpdate) {
        return null;
      }
      return this.SFDCPaymentTypeToVariableName[this.selectedPaymentType];
    },

    subtotal() {
      if (this.isPmUpdate) {
        return 0;
      }
      return this.productCostsInfo.order_totals[this.paymentType];
    },

    creditCardLimitExceeded() {
      return (
        !this.isPmUpdate &&
        this.subtotal > (this.creditCardLimit[this.currencyIsoCode] || Infinity)
      );
    },

    filteredAvailablePaymentMethods() {
      // Setup payment methods for drop down select and prevent credit card when the limit is exceeded
      return this.availablePaymentMethods.reduce(
        (filteredAvailablePaymentMethods, paymentMethod) => {
          if (
            !this.creditCardLimitExceeded ||
            (this.creditCardLimitExceeded &&
              snakeCase(paymentMethod) !== "credit_card")
          ) {
            filteredAvailablePaymentMethods.push({
              displayName:
                this.checkoutSharedContent.paymentMethodNames[
                  snakeCase(paymentMethod)
                ],
              code: paymentMethod,
            });
          }
          return filteredAvailablePaymentMethods;
        },
        []
      );
    },

    disclaimer() {
      let disclaimer =
        this.checkoutSharedContent.updatePaymentMethodDisclaimer.split("__/__");

      let disclaimerText = disclaimer[0];
      if (this.softwareCart.length == 0) {
        disclaimerText = "";
      }
      // Show the no payment method update text for ENT customers
      if (this.isENT) {
        disclaimerText = disclaimer[1];
      }
      let ccFeeText = "";
      if (
        this.checkoutType === MidmarketCheckout &&
        this.currencyIsoCode === "USD" &&
        this.selectedPaymentType != "Upfront Payments"
      ) {
        ccFeeText = this.checkoutContent.ccAdditionalFeeNote;
      }
      return `${disclaimerText} ${ccFeeText}`;
    },

    showSelectPayment() {
      return this.isENT && this.currencyIsoCode?.toUpperCase() === "USD";
    },
  },

  methods: {
    ...mapMutations("checkout", ["setPaymentMethod"]),

    ...mapMutations("billing", ["setPONumber"]),

    handlePOChange(e) {
      this.setPONumber(e.target.value);
    },

    handlePDFChange() {
      this.$emit("update-send-pdf", !this.sendPDF);
      this.sendPDF = !this.sendPDF;
    },
    setFirstFilteredPaymentMethod() {
      if(this.filteredAvailablePaymentMethods?.[0]){
        this.setPaymentMethod(this.filteredAvailablePaymentMethods[0].code);
      }
    },
  },
  created(){
    this.setFirstFilteredPaymentMethod()
  }
};
</script>

<style lang="scss" scoped>
#payment-method-notice-wrapper {
  padding: 10px;
  font-size: 18px;
  max-width: 100%;
  margin: 0;
}
#payment-method-notice-wrapper > p {
  margin: 0;
}

#payment-method-title {
  font-weight: 500;
  font-size: 24px;
  padding-bottom: 4px;
  padding-left: 0;
  border-left: 0px solid;
  border-right: 0px solid;
  border-top: 0px solid;
  border-bottom: 2px solid #f0f0f0;
  margin-bottom: 20px;
}

.payment-select-wrapper ::v-deep svg path {
  fill: #0384fb;
  stroke-width: 1px;
}

.payment-method-wrapper {
  margin-left: 11px;
}

#disclaimer {
  color: #302f32;
  padding-bottom: 20px;
}

.po-wrapper label {
  font-size: 15px;
  margin: 0;
}

.po-wrapper input {
  max-width: 100%;
  margin: 0;
  padding: 5px 10px;
}

.po-notice {
  margin-right: 11px;
  margin-top: 20px;
  border-radius: 5px;
  padding: 10px 20px;
  background-color: #f2f7ff;
  max-width: 78%;
}

.po-notice p {
  margin: 0;
  margin-bottom: 10px;
}

.po-notice a {
  color: #0384fb;
  text-decoration: underline;
}

@media (min-width: 736px) {
  .payment-select-wrapper {
    flex-wrap: nowrap !important;
    justify-content: space-between;
    padding-right: 0;
  }

  .po-wrapper {
    align-items: center;
  }

  .po-wrapper label {
    font-size: 18px;
    margin: 0;
  }

  .po-wrapper input {
    max-width: 220px;
    margin: 0;
    margin-left: 10px;
  }
}

.cc-notice-wrapper {
  padding: 15px;
  background-color: #f2f7ff;
  border-radius: 5px;
  margin-bottom: 20px;
}

::v-deep {
  .payment-method-input {
    margin: 0px;
    padding-left: 0px;
    padding-right: 0px;

    .vs__dropdown-toggle {
      border: 1px solid #c1c9d1;
      height: 48px;
    }

    .vs__selected-options {
      overflow-x: hidden;
    }

    .vs__clear {
      display: none;
    }

    .vs__selected {
      white-space: nowrap;
    }

    .vs__dropdown-menu {
      min-width: 0px;
    }
  }
}
</style>
